<template>
    <teleport to="body">
        <div v-if="modelValue" class="info-overlay" @keydown.esc="close">
            <InfoPopup v-model="showInfoPopup" title="Warning!" :showConfirmButton="false" :showCancelButton="true">
                <div>{{ infoPopupMessage }}</div>
            </InfoPopup>
            <div class="info-modal" role="dialog">
                <header class="info-header">
                    <h3>{{ infoTitle }}</h3>
                    <button class="info-close" @click="close">✕</button>
                </header>
                <section class="info-body">
                    <template v-if="infoTitle !== 'Congratulations!'">
                        <div class=" form-section">
                            <p>Save the voice to finish the Voice Design!</p>
                            <div class="form-group audio-section">
                                <audio controls :src="audioUrl"></audio>
                            </div>
                            <div class="form-group">
                                <label for="voiceName">Voice Name</label>
                                <input id="voiceName" v-model="voiceName" type="text" placeholder="Enter voice name" />
                            </div>
                            <div class="form-group">
                                <label for="voiceTags">Tags</label>
                                <input id="voiceTags" v-model="voiceTags" type="text"
                                    placeholder="Comma-separated tags" />
                            </div>
                        </div>
                    </template>
                    <template v-else>
                        <div class="congrats-message">
                            <p>{{ infoMessage }}</p>
                        </div>
                    </template>
                </section>
                <footer class="info-footer">
                    <template v-if="infoTitle !== 'Congratulations!'">
                        <button class="info-exit info-cancel" @click="cancelAndClose">Cancel</button>
                        <button class="info-exit info-confirm" :class="{ loading: isSaving }" @click="confirmSave"
                            :disabled="!voiceName || isSaving" :aria-busy="isSaving ? 'true' : 'false'">
                            <span v-if="isSaving" class="button-spinner" aria-hidden="true"></span>
                            <span>{{ isSaving ? 'Saving' : 'Save' }}</span>
                        </button>
                    </template>
                    <template v-else>
                        <button class="info-exit info-cancel" @click="goToVD">Not yet</button>
                        <button class="info-exit info-confirm" @click="goToTTS">OK</button>
                    </template>
                </footer>
            </div>
        </div>
    </teleport>
</template>

<script>
import { defineComponent, reactive, ref } from 'vue';
import { useRouter } from 'vue-router';
import { UploadUserVoice } from '@/api/services'
import { checkInput } from '@/utils/InputChecker';
import InfoPopup from '@/components/common/InfoPopup.vue';

export default defineComponent({
    name: 'SaveVoicePopup',
    components: { InfoPopup },
    props: {
        modelValue: { type: Boolean, required: true },
        audioUrl: { type: String, required: true },
        instructText: { type: String, required: true },
    },
    emits: ['update:modelValue', 'cancel', 'saved'],
    setup(props, { emit }) {
        const router = useRouter();
        const values = reactive({});
        const voiceName = ref('');
        const voiceTags = ref('');

        const infoTitle = ref('Save Voice');
        const infoMessage = ref('');
        const isSaving = ref(false);

        const showInfoPopup = ref(false);
        const infoPopupMessage = ref('');

        function goBack() {
            router.push({ name: 'text-prompting' });
        }
        function goToTTS() {
            router.push({ name: 'text-to-speech projects' });
        }
        function goToVD() {
            router.push({ name: 'voice-designer' });
        }
        function arrayBufferToBase64(buffer) {
            let binary = '';
            const bytes = new Uint8Array(buffer);
            for (let i = 0; i < bytes.byteLength; i++) {
                binary += String.fromCharCode(bytes[i]);
            }
            return btoa(binary);
        }
        async function getResponseErrorMessage(response) {
            let result = null;
            try {
                result = await response.json();
            } catch (err) {
                return `${response.status} ${response.statusText || 'Request failed'}`.trim();
            }

            const detail = result?.detail;
            const message = detail?.error?.error
                || detail?.error
                || detail
                || result?.error
                || result?.message
                || result;

            if (typeof message === 'string') {
                return message;
            }

            try {
                return JSON.stringify(message);
            } catch (err) {
                return `${response.status} ${response.statusText || 'Request failed'}`.trim();
            }
        }

        async function confirmSave() {
            if (isSaving.value) return;
            const model = 'IndexTTS2';
            const tag = voiceTags.value;

            const nameLengthLimit = 30;
            const checkName = checkInput(voiceName.value, nameLengthLimit);
            if (!checkName.isLongEnough) {
                infoPopupMessage.value = `Name is too long (${voiceName.value.length} / ${nameLengthLimit} characters). Please shorten it.`;
                showInfoPopup.value = true;
                return;
            }
            if (!checkName.hasOnlyAllowed) {
                infoPopupMessage.value = "Name contains invalid characters: " + checkName.disallowedChars.join(' ') + ". Only English letters, numbers, and common punctuation are allowed.";
                showInfoPopup.value = true;
                return;
            }

            const tagLengthLimit = 30;
            const checkTag = checkInput(tag, tagLengthLimit);
            if (!checkTag.isLongEnough) {
                infoPopupMessage.value = `Tag is too long (${tag.length} / ${tagLengthLimit} characters). Please shorten it.`;
                showInfoPopup.value = true;
                return;
            }
            if (!checkTag.hasOnlyAllowed) {
                infoPopupMessage.value = "Tag contains invalid characters: " + checkTag.disallowedChars.join(' ') + ". Only English letters, numbers, and common punctuation are allowed.";
                showInfoPopup.value = true;
                return;
            }
            try {
                isSaving.value = true;
                const res = await fetch(props.audioUrl);
                const blob = await res.blob();
                const arrayBuffer = await blob.arrayBuffer();
                const base64Audio = arrayBufferToBase64(arrayBuffer);
                const response = await UploadUserVoice(base64Audio, voiceName.value, model, tag, props.instructText);

                if (!response || !response.ok) {
                    const errText = response
                        ? await getResponseErrorMessage(response)
                        : 'Authentication is required.';
                    throw new Error(errText);
                }
                const result = await response.json();
                emit('saved', result);
                infoTitle.value = 'Congratulations!';
                infoMessage.value = '🎉 You have successfully saved your new voice named "' + voiceName.value + '". Would you like to try it in TTS Projects?';
            } catch (err) {
                console.error('Save voice error:', err);
                const message = err.message || String(err);
                infoPopupMessage.value = `Save voice failed: ${message}`;
                showInfoPopup.value = true;
            } finally {
                isSaving.value = false;
            }
        }
        function close() {
            emit('cancel');
            emit('update:modelValue', false);
        }

        function cancelAndClose() {
            emit('cancel');
            emit('update:modelValue', false);
        }
        return {
            infoTitle,
            infoMessage,
            goBack,
            goToTTS,
            goToVD,
            voiceName,
            voiceTags,
            confirmSave,
            close,
            cancelAndClose,
            values,
            isSaving,
            showInfoPopup,
            infoPopupMessage,
        };
    }
});
</script>

<style scoped>
.info-overlay {
    position: fixed;
    inset: 0;
    background: rgba(0, 0, 0, 0.45);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 10000;
}

.info-modal {
    background: #fff;
    width: 500px;
    border-radius: 8px;
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.2);
    display: flex;
    flex-direction: column;
    overflow: hidden;
}

.info-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 1rem;
    border-bottom: 1px solid var(--border-color);
}

.info-close {
    background: none;
    border: none;
    font-size: 1.2rem;
    cursor: pointer;
}

.info-body {
    padding: 1rem;
    flex: 1;
    overflow-y: auto;
}

.info-footer {
    padding: 0.75rem 1rem;
    border-top: 1px solid var(--border-color);
    display: flex;
    justify-content: flex-end;
}

.info-exit {
    margin-left: 20px;
    padding: 6px 14px;
    min-width: 82px;
    background: #fff;
    border: 1px solid transparent;
    border-radius: 4px;
    cursor: pointer;
    transition: background .2s;
}

.info-exit:hover:not(:disabled) {
    background: #f5f5f5;
}

.info-exit:disabled {
    cursor: not-allowed;
    opacity: 0.75;
}

.info-confirm.loading {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
}

.button-spinner {
    width: 14px;
    height: 14px;
    border: 2px solid rgba(255, 255, 255, 0.55);
    border-top-color: #fff;
    border-radius: 50%;
    animation: save-spin 0.75s linear infinite;
}

@keyframes save-spin {
    to {
        transform: rotate(360deg);
    }
}

.form-section {
    margin-top: 18px;
    padding: 18px 20px;
    background: #fff;
    border-radius: 8px;
    gap: 1rem;
    box-shadow: 0 2px 8px rgba(109, 100, 255, 0.06);
}

.audio-section {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    margin-bottom: 1rem;
}

.form-group {
    margin-top: 1rem;
    display: flex;
    flex-direction: column;
    gap: 1rem;

}

.form-group label {
    font-weight: 600;
    font-size: 1rem;
    margin-bottom: 6px;
    letter-spacing: 0.02em;
}

.form-group input[type="text"] {
    padding: 12px 16px;
    border: 1.5px solid var(--purple, #6d64ff);
    border-radius: 6px;
    font-size: 1.08rem;
    background: #fff;
    transition: border 0.2s, box-shadow 0.2s;
    outline: none;
    box-shadow: 0 1px 4px rgba(109, 100, 255, 0.04);
}

.form-group input[type="text"]:focus {
    border-color: #a084ff;
    box-shadow: 0 2px 8px rgba(109, 100, 255, 0.12);
    background: #f5f3ff;
}

.input-field {
    display: flex;
    flex-direction: column;
}

.input-field label {
    margin-bottom: 0.25rem;
    font-weight: 600;
    font-size: 0.95rem;
    color: var(--text-color, #333);
}

.input-field input,
.input-field textarea {
    padding: 0.5rem 0.75rem;
    border: 1px solid var(--border-color, #ccc);
    border-radius: 4px;
    font-size: 1rem;
    width: 100%;
    box-sizing: border-box;
}

.input-field textarea {
    resize: vertical;
    min-height: 80px;
}

.info-confirm {
    background-color: var(--purple);
    color: white;
    font-weight: 500;
}

.info-confirm:hover {
    background-color: var(--purple-hover);
    color: white;
}
</style>
